import {apiRequest} from './api';
import {cdnStringEndpoint} from '../constants/endpoints';
import {v4 as uuidv4} from 'uuid';

export interface UploadConfig {
  originBaseUrl: string;
  uploadUrl: string;
}

type UploadParams = {
  sasUrl: UploadConfig;
  file: File; // Web uses File objects
  /** Blob name; a random one when not given. */
  fileName?: string;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
};

export const ACTIVITIES_PHOTOS_CONTAINER = 'blobcontaineractivitiesphotos';
/** Where the apps keep profile photos. */
export const PROFILE_PHOTOS_CONTAINER = 'blobcontaineruserprofilephoto';

export const getSasUrl = async (
  blobContainerName: string = ACTIVITIES_PHOTOS_CONTAINER,
) => {
  const response = await apiRequest<UploadConfig | null>(
    {
      method: 'GET',
      endpoint: cdnStringEndpoint,
      queryParams: {
        blobContainerName,
      },
    },
    {
      errorMessage: 'Failed to fetch CDN SAS string',
      defaultValue: null,
    },
  );

  return response?.data ?? null;
};

// Helper to compress images using Canvas
export const compressImage = (
  file: File,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 1,
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          blob =>
            blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
          file.type,
          quality,
        );
      };
    };
    reader.onerror = e => reject(e);
  });
};

export const uploadToBlob = async ({
  sasUrl,
  file,
  fileName,
  maxHeight,
  maxWidth,
  quality,
}: UploadParams): Promise<string> => {
  if (!file || !sasUrl) throw new Error('Missing file or SAS URL');

  const u = new URL(sasUrl.uploadUrl);
  const qs = new URLSearchParams(u.search);
  const safeName = encodeURIComponent(fileName ?? uuidv4());
  const containerUrl = `${u.origin}${u.pathname.replace(/\/$/, '')}`;
  const uploadUrl = `${containerUrl}/${safeName}?${qs.toString()}`;

  try {
    // setIsUploading(true);
    // setError(null);

    let body: Blob | File = file;

    // Compress if it's an image
    if (file.type.startsWith('image/')) {
      body = await compressImage(file, maxWidth, maxHeight, quality);
    }

    // Standard browser fetch for Azure PUT request
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'x-ms-blob-type': 'BlockBlob',
        'x-ms-version': qs.get('sv') || '2023-11-03',
      },
      body: body,
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status: ${response.status}`);
    }

    // Return the URL without the SAS token for storage in your DB
    return `${containerUrl}/${safeName}`;
  } catch (e) {
    const err = e instanceof Error ? e : new Error(String(e));
    // setError(err);
    throw err;
  } finally {
    // setIsUploading(false);
  }
};

//   return {uploadToBlob, isUploading, error, sasUrl};
// };
